import React from "react";
import { useRef, useState } from 'react';
import { vencimientosApi } from './api';
import VehiculoCatalogPicker from './VehiculoCatalogPicker';

/* Wizard "Nuevo cliente externo" (Flujo C — captura manual, sin IA). 4 pasos:
   1) Cliente (autocomplete de cliente existente o alta nueva) + vehículo
   2) Seguro: aseguradora/póliza + años + monto + 1ª fecha → el backend proyecta las primas
   3) Placas: entidad/placa + vigencia (manual)
   4) GPS: diferido
   "Guardar cliente" → POST /seguros-dashboard/cliente (crea/reusa User → Budget → Vehicle → Insurance(+primas) → Plate). */

const STEPS = ['Cliente', 'Seguro', 'Placas', 'GPS'];

export default function NuevoClienteWizard({ onClose, toast, onCreated }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Cliente: user_id != null => existente (autocomplete); si no, alta nueva.
  const [cliente, setCliente] = useState({ user_id: null, name: '', email: '', phone: '' });
  const [vehicle, setVehicle] = useState({ brand: '', version: '', year: '', vin: '' });
  const [vehCatalog, setVehCatalog] = useState({}); // lo que emite el picker del catálogo
  const [seguro, setSeguro] = useState({ company: '', policy_number: '', coverage: 'amplia', broker: '', years: '', monto: '', primera_fecha: '' });
  const [placa, setPlaca] = useState({ entidad: '', plate_number: '', expires_at: '', next_renewal_date: '' });

  // Autocomplete
  const [matches, setMatches] = useState([]);
  const [showMatches, setShowMatches] = useState(false);
  const searchTimer = useRef(null);

  const setVeh = (k, v) => setVehicle((s) => ({ ...s, [k]: v }));
  const setSeg = (k, v) => setSeguro((s) => ({ ...s, [k]: v }));
  const setPla = (k, v) => setPlaca((s) => ({ ...s, [k]: v }));

  function onNameChange(value) {
    // Al escribir, es alta nueva (se suelta cualquier cliente existente elegido).
    setCliente((c) => ({ ...c, name: value, user_id: null }));
    setShowMatches(!!value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) { setMatches([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await vencimientosApi.buscarClientes(value.trim());
        setMatches(res?.data?.data || []);
      } catch { setMatches([]); }
    }, 250);
  }

  function pickCliente(m) {
    setCliente({ user_id: m.id, name: m.name, email: m.email || '', phone: '' });
    setShowMatches(false);
    setMatches([]);
  }

  function clearCliente() {
    setCliente({ user_id: null, name: '', email: '', phone: '' });
    setMatches([]);
  }

  async function handleSubmit() {
    if (!cliente.user_id && !cliente.name.trim()) {
      toast?.current?.show?.({ severity: 'warn', summary: 'Falta el cliente', detail: 'Elige uno existente o escribe el nombre.', life: 3000 });
      setStep(1);
      return;
    }
    const payload = {
      user_id: cliente.user_id || null,
      cliente: cliente.user_id ? null : {
        name: cliente.name.trim(),
        email: cliente.email.trim() || null,
        phone: cliente.phone.trim() || null,
      },
      vehicle: {
        brand: vehCatalog.brand || null,
        version: vehCatalog.version || null,
        year: vehCatalog.year || null,
        vin: vehicle.vin.trim() || null,
        condicion: vehCatalog.condicion || null,
        tipo: vehCatalog.tipo || null,
        motor: vehCatalog.motor || null,
      },
      precio: vehCatalog.precio ?? null,
      accesorios: vehCatalog.accesorios ?? null,
      regimen: vehCatalog.regimen || null,
      estado_placa: vehCatalog.estado_placa || null,
      seguro: (seguro.company.trim() || seguro.policy_number.trim()) ? {
        company: seguro.company.trim() || null,
        policy_number: seguro.policy_number.trim() || null,
        coverage: seguro.coverage || null,
        broker: seguro.broker.trim() || null,
        years: seguro.years ? Number(seguro.years) : null,
        monto: seguro.monto !== '' ? Number(seguro.monto) : null,
        primera_fecha: seguro.primera_fecha || null,
      } : null,
      placa: (placa.entidad.trim() || placa.plate_number.trim()) ? {
        entidad: placa.entidad.trim() || null,
        plate_number: placa.plate_number.trim() || null,
        expires_at: placa.expires_at || null,
        next_renewal_date: placa.expires_at || null, // misma fecha: vencer = renovar
      } : null,
    };

    setSaving(true);
    try {
      await vencimientosApi.crearClienteExterno(payload);
      toast?.current?.show?.({ severity: 'success', summary: 'Cliente guardado', life: 2500 });
      onCreated?.();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.message || 'No se pudo guardar el cliente.';
      toast?.current?.show?.({ severity: 'error', summary: 'Error al guardar', detail: msg, life: 3500 });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="venc-wz-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="venc-wz" role="dialog" aria-modal="true">
        <div className="dh">
          <div className="veh-ic"><i className="pi pi-user-plus" /></div>
          <div className="ttl">
            <h3>Nuevo cliente externo</h3>
            <div className="meta">Flujo C · captura manual</div>
          </div>
          <button type="button" className="x" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className="wz-steps">
          {STEPS.map((s, i) => (
            <div className="st-wrap" key={s} style={{ display: 'contents' }}>
              <div className={`st ${step === i + 1 ? 'on' : ''} ${step > i + 1 ? 'done' : ''}`}>
                <span className="n">{step > i + 1 ? <i className="pi pi-check" /> : i + 1}</span>
                <span className="lab">{s}{i === 3 && ' · pronto'}</span>
              </div>
              {i < STEPS.length - 1 && <div className="conn" />}
            </div>
          ))}
        </div>

        <div className="dbody">
          {step === 1 && (
            <div className="form-grid">
              {cliente.user_id ? (
                <div className="field wide">
                  <label>Cliente</label>
                  <div className="ac-picked">
                    <b>{cliente.name}</b>
                    <button type="button" className="venc-btn ghost sm" onClick={clearCliente}>Cambiar</button>
                  </div>
                  <span className="hint-mini">Se le agregará un carro a este cliente existente.</span>
                </div>
              ) : (
                <div className="field wide ac-wrap">
                  <label>Razón social / Nombre (PFAE)</label>
                  <input
                    placeholder="Busca un cliente existente o escribe uno nuevo"
                    value={cliente.name}
                    onChange={(e) => onNameChange(e.target.value)}
                    onFocus={() => setShowMatches(!!cliente.name)}
                  />
                  {showMatches && (
                    <div className="ac-list">
                      {matches.length === 0 ? (
                        <div className="ac-item"><b>Crear cliente nuevo</b><span>Sin coincidencias — se dará de alta como externo</span></div>
                      ) : (
                        matches.map((m) => (
                          <div className="ac-item" key={m.id} onClick={() => pickCliente(m)}>
                            <b>{m.name}</b><span>{m.email || '—'} · {m.carros} carro(s)</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {!cliente.user_id && (
                <>
                  <div className="field"><label>Teléfono</label>
                    <input placeholder="55 0000 0000" value={cliente.phone} onChange={(e) => setCliente((c) => ({ ...c, phone: e.target.value }))} /></div>
                  <div className="field"><label>Correo</label>
                    <input placeholder="contacto@empresa.mx" value={cliente.email} onChange={(e) => setCliente((c) => ({ ...c, email: e.target.value }))} /></div>
                </>
              )}

              {/* Catálogo de autos reusado del cotizador (aislado): Condición→Tipo→Año→Marca→Tipo→Versión + motor/régimen/estado/precio */}
              <VehiculoCatalogPicker onChange={setVehCatalog} />
              <div className="field"><label>VIN</label>
                <input placeholder="3TYKZ5AN9PT012845" value={vehicle.vin} onChange={(e) => setVeh('vin', e.target.value)} /></div>
            </div>
          )}

          {step === 2 && (
            <div className="form-grid">
              <div className="field"><label>Aseguradora</label>
                <input placeholder="Qualitas" value={seguro.company} onChange={(e) => setSeg('company', e.target.value)} /></div>
              <div className="field"><label>Número de póliza</label>
                <input placeholder="2370152509" value={seguro.policy_number} onChange={(e) => setSeg('policy_number', e.target.value)} /></div>
              <div className="field"><label>Cobertura</label>
                <select value={seguro.coverage} onChange={(e) => setSeg('coverage', e.target.value)}>
                  <option value="amplia">Amplia</option>
                  <option value="limitada">Limitada</option>
                  <option value="RC">RC</option>
                </select></div>
              <div className="field"><label>Broker</label>
                <input placeholder="Lockton" value={seguro.broker} onChange={(e) => setSeg('broker', e.target.value)} /></div>
              <div className="field"><label>Años de la póliza</label>
                <input type="number" min="1" max="20" placeholder="3" value={seguro.years} onChange={(e) => setSeg('years', e.target.value)} /></div>
              <div className="field"><label>Monto por año</label>
                <input type="number" step="0.01" placeholder="16734.42" value={seguro.monto} onChange={(e) => setSeg('monto', e.target.value)} /></div>
              <div className="field"><label>1ª fecha de vencimiento</label>
                <input type="date" value={seguro.primera_fecha} onChange={(e) => setSeg('primera_fecha', e.target.value)} /></div>
              <div className="field wide">
                <span className="hint-mini">El sistema genera las primas: una por año, +1 año desde la 1ª fecha, con ese monto. (Puedes ajustarlas después con "Completar / editar prima".)</span>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-grid">
              <div className="field"><label>Entidad</label>
                <input placeholder="CDMX" value={placa.entidad} onChange={(e) => setPla('entidad', e.target.value)} /></div>
              <div className="field"><label>Placas</label>
                <input placeholder="PXM-01-44" value={placa.plate_number} onChange={(e) => setPla('plate_number', e.target.value)} /></div>
              <div className="field"><label>Vencimiento de la tarjeta</label>
                <input type="date" value={placa.expires_at} onChange={(e) => setPla('expires_at', e.target.value)} /></div>
              <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <span className="hint-mini">La tarjeta se renueva cuando vence: esta fecha es también la próxima renovación.</span></div>
            </div>
          )}

          {step === 4 && (
            <div className="empty-mini" style={{ padding: 40 }}>
              <i className="pi pi-map-marker" style={{ fontSize: 26, display: 'block', marginBottom: 12, color: 'var(--pei-gray-2)' }} />
              <h4 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 600, color: 'var(--pei-text)' }}>GPS</h4>
              <p style={{ margin: 0 }}>Paso diferido. Puedes guardar el cliente con su seguro y placas; el GPS se capturará cuando el módulo esté listo.</p>
            </div>
          )}
        </div>

        <div className="df">
          {step > 1 && (
            <button type="button" className="venc-btn ghost sm" onClick={() => setStep(step - 1)}>
              <i className="pi pi-arrow-left" />Atrás
            </button>
          )}
          <span className="grow" />
          <button type="button" className="venc-btn ghost sm" onClick={onClose}>Cancelar</button>
          {step < 4 ? (
            <button type="button" className="venc-btn primary sm" onClick={() => setStep(step + 1)}>
              Continuar<i className="pi pi-arrow-right" />
            </button>
          ) : (
            <button type="button" className="venc-btn primary sm" onClick={handleSubmit} disabled={saving}>
              <i className="pi pi-check" />{saving ? 'Guardando…' : 'Guardar cliente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
